import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueries } from '@tanstack/react-query'
import { ArrowLeft, Settings2, X, Video, FileText, Sparkles, ClipboardCheck, FlaskConical, BarChart3, Microscope } from 'lucide-react'
import { learnerApi, type LearnerClassCourse } from '@/api/b2b'
import { quizScriptApi } from '@/api/quizScript'
import { contentApi, type ContentHierarchyNode } from '@/api/content'
import { ReviewContentSidebar } from '@/components/learner/ReviewContentSidebar'
import { TotalQuizSession, type NavPolicy, type QuizDebugContext } from '@/components/learner/TotalQuizSession'
import { VideoReviewSection } from '@/components/learner/VideoReviewSection'
import { CsvComparisonView } from '@/components/learner/CsvComparisonView'
import { AiqPoolAnalysis } from '@/components/learner/AiqPoolAnalysis'
import { AiqDomainAnalysis } from '@/components/learner/AiqDomainAnalysis'
import { cn } from '@/lib/utils'
import type { QuizScript } from '@quebon/shared-learning'

type ReviewMode = 'quiz' | 'video' | 'aiq' | 'juneaiq' | 'csv'
type AiqSubMode = 'quiz' | 'analysis' | 'domain'

export default function QuizReviewPage() {
  const { t } = useTranslation('learner')
  const navigate = useNavigate()

  const [selectedLessonId, setSelectedLessonId] = useState<number | undefined>()
  const [selectedContentId, setSelectedContentId] = useState<number | undefined>()
  const [navPolicy, setNavPolicy] = useState<NavPolicy>('free_move')
  const [showSettings, setShowSettings] = useState(false)
  const [reviewMode, setReviewMode] = useState<ReviewMode>('quiz')
  const [includeInactive, setIncludeInactive] = useState(false)

  // AIQ 모드용: 선택된 classCourse
  const [selectedClassCourse, setSelectedClassCourse] = useState<LearnerClassCourse | undefined>()
  const [aiqSubMode, setAiqSubMode] = useState<AiqSubMode>('quiz')

  // lessonId 변경 시 quiz navigation 초기화
  useEffect(() => {
    setNavPolicy('free_move')
  }, [selectedLessonId])

  const { data: classCoursesData, isLoading: isLoadingCourses } = useQuery({
    queryKey: ['myClassCourses'],
    queryFn: () => learnerApi.getMyClassCourses(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  const classCourses = classCoursesData?.data?.data || []

  // CSV 모드: 모든 contentId 추출 (bundle인 경우 bundleContents에서, 아니면 직접)
  const allContentIds = useMemo(() => {
    const ids = new Set<number>()
    for (const cc of classCourses) {
      if (cc.isBundle && cc.bundleContents) {
        for (const bc of cc.bundleContents) {
          if (bc.contentId) ids.add(bc.contentId)
        }
      } else if (cc.contentId) {
        ids.add(cc.contentId)
      }
    }
    return Array.from(ids)
  }, [classCourses])

  // CSV 모드: 모든 curriculum hierarchy를 병렬 조회
  const allHierarchyQueries = useQueries({
    queries: reviewMode === 'csv'
      ? allContentIds.map(id => ({
          queryKey: ['contentHierarchy', id, { includeInactive }],
          queryFn: () => contentApi.getContentHierarchy(id, includeInactive),
          staleTime: 10 * 60 * 1000,
        }))
      : [],
  })

  const allHierarchies = useMemo(() => {
    return allHierarchyQueries
      .filter(q => q.data != null)
      .map(q => q.data!)
  }, [allHierarchyQueries])

  const isLoadingHierarchies = allHierarchyQueries.some(q => q.isLoading)

  const { data: lessonDetail } = useQuery({
    queryKey: ['lessonDetail', selectedLessonId],
    queryFn: () => contentApi.getLessonDetail(selectedLessonId!),
    enabled: !!selectedLessonId,
  })

  const { data: quizList, isLoading: isLoadingQuizzes } = useQuery<QuizScript[]>({
    queryKey: ['wrapupQuizzes', selectedLessonId],
    queryFn: () => quizScriptApi.getWrapupQuizzes(selectedLessonId!, false, undefined, false),
    enabled: !!selectedLessonId,
  })

  // AIQ 모드: product 단위 AIQ 퀴즈 조회
  const { data: aiqQuizList, isLoading: isLoadingAiqQuizzes } = useQuery<QuizScript[]>({
    queryKey: ['productAiqQuizzes', selectedClassCourse?.productId],
    queryFn: () => quizScriptApi.getProductAiqQuizzes(selectedClassCourse!.productId, 9999, false, false),
    enabled: reviewMode === 'aiq' && !!selectedClassCourse?.productId,
  })

  // AIQ by June 모드: title에 [JUNEAIQ] 포함된 전체 퀴즈 조회
  const { data: juneAiqQuizList, isLoading: isLoadingJuneAiq } = useQuery<QuizScript[]>({
    queryKey: ['juneAiqQuizzes'],
    queryFn: () => quizScriptApi.searchQuizzesByTitle('[JUNEAIQ]', false),
    enabled: reviewMode === 'juneaiq',
    staleTime: 5 * 60 * 1000,
  })

  // Content Hierarchy 조회 (디버그 컨텍스트용)
  const { data: hierarchy } = useQuery({
    queryKey: ['contentHierarchy', selectedContentId, { includeInactive: includeInactive }],
    queryFn: () => contentApi.getContentHierarchy(selectedContentId!, includeInactive),
    enabled: !!selectedContentId,
    staleTime: 10 * 60 * 1000,
  })

  // 선택된 레슨의 경로 정보 추출 (curriculum -> course -> module -> lesson)
  const debugContext = useMemo((): QuizDebugContext | undefined => {
    if (!hierarchy || !selectedLessonId) return undefined

    const path: { node: ContentHierarchyNode; type: string }[] = []

    const findPath = (nodes: ContentHierarchyNode[], targetId: number): boolean => {
      for (const node of nodes) {
        if (node.id === targetId && node.type === 'lesson') {
          path.push({ node, type: node.type })
          return true
        }
        if (node.children.length > 0 && findPath(node.children, targetId)) {
          path.unshift({ node, type: node.type })
          return true
        }
      }
      return false
    }

    // hierarchy.children에서 검색
    findPath(hierarchy.children, selectedLessonId)

    // 최상위 hierarchy 정보도 추가
    const context: QuizDebugContext = {
      contentId: selectedContentId,
    }

    // 최상위가 curriculum인 경우
    if (hierarchy.type === 'curriculum') {
      context.curriculumId = hierarchy.id
      context.curriculumName = hierarchy.bareTitle || hierarchy.title
    } else if (hierarchy.type === 'course') {
      context.courseId = hierarchy.id
      context.courseName = hierarchy.bareTitle || hierarchy.title
    }

    // 경로에서 정보 추출
    for (const { node, type } of path) {
      switch (type) {
        case 'curriculum':
          context.curriculumId = node.id
          context.curriculumName = node.bareTitle || node.title
          break
        case 'course':
          context.courseId = node.id
          context.courseName = node.bareTitle || node.title
          break
        case 'module':
          context.moduleId = node.id
          context.moduleName = node.bareTitle || node.title
          break
        case 'lesson':
          context.lessonId = node.id
          context.lessonTitle = node.bareTitle || node.title
          break
      }
    }

    // lessonDetail에서 qid 추가
    if (lessonDetail?.qid) {
      context.qid = lessonDetail.qid
    }

    return context
  }, [hierarchy, selectedLessonId, selectedContentId, lessonDetail])

  // AIQ 모드용 디버그 컨텍스트
  const aiqDebugContext = useMemo((): QuizDebugContext | undefined => {
    if (!selectedClassCourse) return undefined
    return {
      contentId: selectedClassCourse.contentId ?? undefined,
    }
  }, [selectedClassCourse])

  const handleLessonSelect = (lessonId: number, contentId: number) => {
    setSelectedLessonId(lessonId)
    setSelectedContentId(contentId)
  }

  const handleBack = () => {
    navigate(-1)
  }

  if (isLoadingCourses) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="inline-block w-8 h-8 rounded-full border-b-2 border-indigo-600 animate-spin"></div>
          <p className="mt-4 text-gray-600">{t('quizReview.loading')}</p>
        </div>
      </div>
    )
  }

  if (classCourses.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gray-50">
        <p className="mb-4 text-gray-600">{t('quizReview.noCourses')}</p>
        <button
          onClick={handleBack}
          className="font-medium text-indigo-600 hover:text-indigo-800"
        >
          {t('quizReview.goBack')}
        </button>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* AIQ 모드: classCourse 선택 사이드바 / CSV 모드: 사이드바 없음 / 기본 모드: 레슨 트리 사이드바 */}
      {reviewMode === 'csv' || reviewMode === 'juneaiq' ? null : reviewMode === 'aiq' ? (
        <AiqCourseSidebar
          classCourses={classCourses}
          selectedClassCourse={selectedClassCourse}
          onSelect={setSelectedClassCourse}
          className="flex-shrink-0"
        />
      ) : (
        <ReviewContentSidebar
          classCourses={classCourses}
          currentLessonId={selectedLessonId}
          onLessonSelect={handleLessonSelect}
          includeInactive={includeInactive}
          className="flex-shrink-0"
        />
      )}

      <div className="flex flex-col flex-1 min-w-0">
        <header className="flex justify-between items-center px-6 py-4 bg-white border-b border-gray-200">
          <div className="flex gap-4 items-center">
            <button
              onClick={handleBack}
              className="p-2 text-gray-500 rounded-lg transition-colors hover:text-gray-700 hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{t('quizReview.contentReview')}</h1>
              <p className="text-sm text-gray-500">{t('quizReview.fullContentReview')}</p>
            </div>
          </div>

          {/* Mode Toggle */}
          <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
            <button
              onClick={() => setReviewMode('video')}
              className={cn(
                'flex gap-1.5 items-center px-3 py-1.5 text-sm font-medium rounded-md transition-all',
                reviewMode === 'video'
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              <Video className="w-4 h-4" />
              {t('quizReview.video')}
            </button>
            <button
              onClick={() => setReviewMode('quiz')}
              className={cn(
                'flex gap-1.5 items-center px-3 py-1.5 text-sm font-medium rounded-md transition-all',
                reviewMode === 'quiz'
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              <FileText className="w-4 h-4" />
              {t('quizReview.quiz')}
            </button>
            <button
              onClick={() => setReviewMode('aiq')}
              className={cn(
                'flex gap-1.5 items-center px-3 py-1.5 text-sm font-medium rounded-md transition-all',
                reviewMode === 'aiq'
                  ? 'bg-white text-amber-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              <Sparkles className="w-4 h-4" />
              AIQ
            </button>
            <button
              onClick={() => setReviewMode('juneaiq')}
              className={cn(
                'flex gap-1.5 items-center px-3 py-1.5 text-sm font-medium rounded-md transition-all',
                reviewMode === 'juneaiq'
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              <FlaskConical className="w-4 h-4" />
              AIQ by June
            </button>
            <button
              onClick={() => setReviewMode('csv')}
              className={cn(
                'flex gap-1.5 items-center px-3 py-1.5 text-sm font-medium rounded-md transition-all',
                reviewMode === 'csv'
                  ? 'bg-white text-emerald-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              <ClipboardCheck className="w-4 h-4" />
              {t('quizReview.csvComparison')}
            </button>
          </div>

          <div className="flex gap-2 items-center">
            <div className="relative">
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={cn(
                  'p-2 rounded-lg transition-colors',
                  showSettings ? 'text-indigo-600 bg-indigo-100' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                )}
              >
                <Settings2 className="w-5 h-5" />
              </button>

              {showSettings && (
                <div className="absolute right-0 top-full z-10 p-4 mt-2 w-64 bg-white rounded-lg border border-gray-200 shadow-lg">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm font-medium text-gray-700">{t('quizReview.settings')}</span>
                    <button
                      onClick={() => setShowSettings(false)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="flex gap-2 items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={includeInactive}
                          onChange={(e) => setIncludeInactive(e.target.checked)}
                          className="w-4 h-4 text-indigo-600 rounded"
                        />
                        <span className="text-sm text-gray-700">{t('quizReview.includeInactive')}</span>
                      </label>
                    </div>
                    <div>
                      <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                        {t('quizReview.navPolicy')}
                      </label>
                      <div className="mt-2 space-y-2">
                        <label className="flex gap-2 items-center cursor-pointer">
                          <input
                            type="radio"
                            name="navPolicy"
                            value="free_move"
                            checked={navPolicy === 'free_move'}
                            onChange={() => setNavPolicy('free_move')}
                            className="w-4 h-4 text-indigo-600"
                          />
                          <span className="text-sm text-gray-700">{t('quizReview.freeMove')}</span>
                        </label>
                        <label className="flex gap-2 items-center cursor-pointer">
                          <input
                            type="radio"
                            name="navPolicy"
                            value="lock_next"
                            checked={navPolicy === 'lock_next'}
                            onChange={() => setNavPolicy('lock_next')}
                            className="w-4 h-4 text-indigo-600"
                          />
                          <span className="text-sm text-gray-700">{t('quizReview.sequentialProgress')}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="overflow-hidden flex-1 bg-white">
          {reviewMode === 'csv' ? (
            // CSV 대조 모드: 모든 curriculum hierarchy 자동 로딩
            <CsvComparisonView
              hierarchies={allHierarchies}
              isLoading={isLoadingHierarchies}
            />
          ) : reviewMode === 'juneaiq' ? (
            // AIQ by June 모드: title에 [JUNEAIQ] 포함된 전체 퀴즈
            <TotalQuizSession
              key="juneaiq"
              quizList={juneAiqQuizList ?? []}
              navPolicy={navPolicy}
              title="AIQ by June"
              isLoading={isLoadingJuneAiq}
            />
          ) : reviewMode === 'aiq' ? (
            // AIQ 모드: classCourse 선택 → 문제 풀기 / 출제 분석
            selectedClassCourse ? (
              <div className="flex flex-col h-full">
                {/* AIQ Sub-mode Toggle */}
                <div className="flex gap-2 items-center px-4 py-2 bg-gray-50 border-b border-gray-200">
                  <div className="flex gap-1 p-0.5 bg-gray-100 rounded-md">
                    <button
                      onClick={() => setAiqSubMode('quiz')}
                      className={cn(
                        'flex gap-1 items-center px-2.5 py-1 text-xs font-medium rounded transition-all',
                        aiqSubMode === 'quiz' ? 'bg-white text-amber-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                      )}
                    >
                      <Sparkles className="w-3 h-3" />
                      {t('quizReview.solveQuiz')}
                    </button>
                    <button
                      onClick={() => setAiqSubMode('analysis')}
                      className={cn(
                        'flex gap-1 items-center px-2.5 py-1 text-xs font-medium rounded transition-all',
                        aiqSubMode === 'analysis' ? 'bg-white text-amber-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                      )}
                    >
                      <BarChart3 className="w-3 h-3" />
                      {t('quizReview.quizAnalysis')}
                    </button>
                    <button
                      onClick={() => setAiqSubMode('domain')}
                      className={cn(
                        'flex gap-1 items-center px-2.5 py-1 text-xs font-medium rounded transition-all',
                        aiqSubMode === 'domain' ? 'bg-white text-amber-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                      )}
                    >
                      <Microscope className="w-3 h-3" />
                      {t('quizReview.domainSettings')}
                    </button>
                  </div>
                  <span className="text-xs text-gray-400">
                    {selectedClassCourse.productName || selectedClassCourse.contentTitle || t('quizReview.courseFallback')}
                  </span>
                </div>
                <div className="flex-1 overflow-hidden">
                  {aiqSubMode === 'analysis' ? (
                    <AiqPoolAnalysis
                      key={`analysis-${selectedClassCourse.productId}`}
                      productId={selectedClassCourse.productId}
                    />
                  ) : aiqSubMode === 'domain' ? (
                    <AiqDomainAnalysis
                      key={`domain-${selectedClassCourse.productId}`}
                      productId={selectedClassCourse.productId}
                    />
                  ) : (
                    <TotalQuizSession
                      key={`aiq-${selectedClassCourse.productId}`}
                      quizList={aiqQuizList ?? []}
                      navPolicy={navPolicy}
                      title={`AIQ - ${selectedClassCourse.productName || selectedClassCourse.contentTitle || t('quizReview.courseFallback')}`}
                      isLoading={isLoadingAiqQuizzes}
                      debugContext={aiqDebugContext}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div className="flex justify-center items-center h-full">
                <p className="text-gray-500">{t('quizReview.selectCourse')}</p>
              </div>
            )
          ) : selectedLessonId ? (
            reviewMode === 'video' ? (
              <VideoReviewSection
                key={`video-${selectedLessonId}`}
                contentId={selectedLessonId}
                qid={lessonDetail?.qid}
                lessonTitle={lessonDetail?.bareTitle || lessonDetail?.title}
                debugContext={debugContext}
                isLoading={!lessonDetail}
              />
            ) : (
              <TotalQuizSession
                key={selectedLessonId}
                quizList={quizList ?? []}
                navPolicy={navPolicy}
                title={lessonDetail?.bareTitle || lessonDetail?.title}
                isLoading={isLoadingQuizzes}
                debugContext={debugContext}
              />
            )
          ) : (
            <div className="flex justify-center items-center h-full">
              <p className="text-gray-500">{t('quizReview.selectLesson')}</p>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}

/**
 * AIQ 모드용 사이드바: classCourse 단위 선택
 */
function AiqCourseSidebar({
  classCourses,
  selectedClassCourse,
  onSelect,
  className,
}: {
  classCourses: LearnerClassCourse[]
  selectedClassCourse?: LearnerClassCourse
  onSelect: (course: LearnerClassCourse) => void
  className?: string
}) {
  const { t } = useTranslation('learner')
  // productId 기준으로 중복 제거
  const uniqueCourses = useMemo(() => {
    const seen = new Set<number>()
    return classCourses.filter(c => {
      if (seen.has(c.productId)) return false
      seen.add(c.productId)
      return true
    })
  }, [classCourses])

  return (
    <div className={cn('flex flex-col w-80 h-full bg-gray-50 border-r border-gray-200', className)}>
      <div className="px-4 py-4 border-b border-gray-200">
        <h2 className="text-base font-semibold text-gray-900">{t('quizReview.aiqReview')}</h2>
        <p className="text-xs text-gray-500 mt-0.5">{t('quizReview.courseCount', { count: uniqueCourses.length })}</p>
      </div>

      <div className="overflow-y-auto flex-1">
        {uniqueCourses.map(course => {
          const isSelected = selectedClassCourse?.productId === course.productId
          return (
            <button
              key={course.classProductId}
              onClick={() => onSelect(course)}
              className={cn(
                'flex relative gap-3 items-start px-4 py-3 w-full text-left border-b border-gray-100 transition-all',
                isSelected
                  ? 'bg-amber-50 hover:bg-amber-50'
                  : 'hover:bg-gray-100'
              )}
            >
              {isSelected && (
                <div className="absolute top-0 bottom-0 left-0 w-1 bg-amber-500" />
              )}
              <div className={cn(
                'mt-0.5 flex-shrink-0',
                isSelected ? 'text-amber-600' : 'text-gray-400'
              )}>
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className={cn(
                  'text-sm',
                  isSelected ? 'font-bold text-amber-700' : 'font-medium text-gray-700'
                )}>
                  {course.productName || course.contentTitle || `${t('quizReview.courseFallback')} ${course.productId}`}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {course.className}
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
